import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getHitLabApiMessage } from './hitLabUtils';
import {
  approveAdminVoiceDrop,
  listAdminVoiceDrops,
  normalizeVoiceDropPayload,
  rejectAdminVoiceDrop,
} from './hitLabRadioVoiceDropApi';

const initialData = {
  summary: {},
  rows: [],
};

const formatCount = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Not set';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getStatusLabel = (status) =>
  String(status || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getDropTypeLabel = (dropType) =>
  String(dropType || 'listener_drop')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getSuggestedTitle = (row = {}) => {
  const firstName = String(row.firstName || '').trim() || 'JG Player';
  const cityRegion = String(row.cityRegion || '').trim();
  return cityRegion
    ? `Listener Drop - ${firstName} from ${cityRegion}`
    : `Listener Drop - ${firstName}`;
};

export default function HitLabRadioListenerVoiceDropsAdmin({
  authConfig,
  refreshKey = 0,
}) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [rejectReasons, setRejectReasons] = useState({});
  const [approvedTitles, setApprovedTitles] = useState({});

  const rows = Array.isArray(data.rows) ? data.rows : [];
  const summary = data.summary || {};
  const reviewRows = useMemo(
    () => rows.filter((row) => ['submitted', 'approved', 'rejected'].includes(row.status)),
    [rows],
  );

  const loadVoiceDrops = useCallback(() => {
    if (!authConfig) return Promise.resolve();
    setLoading(true);
    setError('');
    return listAdminVoiceDrops({ authConfig })
      .then((response) => {
        setData(normalizeVoiceDropPayload(response));
      })
      .catch((err) => {
        setError(
          getHitLabApiMessage(
            err,
            'Listener Voice Drops could not be loaded.',
          ),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [authConfig]);

  useEffect(() => {
    void loadVoiceDrops();
  }, [loadVoiceDrops, refreshKey]);

  const approveVoiceDrop = async (row) => {
    if (!authConfig || !row?.id || busyId) return;
    const confirmed = window.confirm(
      'Approve this listener voice drop and publish it into JG Radio Catalog/rotation? This does not issue JGK.',
    );
    if (!confirmed) return;
    const suggestedTitle = getSuggestedTitle(row);
    const approvedTitle = String(approvedTitles[row.id] || '').trim() || suggestedTitle;
    setBusyId(row.id);
    setError('');
    setMessage('');
    try {
      await approveAdminVoiceDrop({
        authConfig,
        id: row.id,
        reviewNote: 'Approved from HitLab admin.',
        approvedTitle,
      });
      setMessage('Voice drop approved and sent to Radio rotation.');
      await loadVoiceDrops();
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Voice drop could not be approved.'));
    } finally {
      setBusyId('');
    }
  };

  const rejectVoiceDrop = async (row) => {
    if (!authConfig || !row?.id || busyId) return;
    const reason = String(rejectReasons[row.id] || '').trim();
    if (!reason) {
      setError('Enter a rejection reason before rejecting this voice drop.');
      return;
    }
    setBusyId(row.id);
    setError('');
    setMessage('');
    try {
      await rejectAdminVoiceDrop({
        authConfig,
        id: row.id,
        reason,
      });
      setMessage('Voice drop rejected.');
      setRejectReasons((current) => ({ ...current, [row.id]: '' }));
      await loadVoiceDrops();
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Voice drop could not be rejected.'));
    } finally {
      setBusyId('');
    }
  };

  return (
    <section
      className="hitlab-radio-voice-drops-admin"
      aria-label="Listener Voice Drops"
    >
      <div className="hitlab-radio-feedback-rewards-admin-header">
        <div>
          <p className="hitlab-kicker">LISTENER VOICE DROPS</p>
          <h2>Listener Voice Drops</h2>
          <p>
            Review optional listener drops after written feedback. Approval
            publishes the drop into existing JG Radio Catalog rotation.
          </p>
        </div>
        <button
          type="button"
          className="hitlab-secondary-btn"
          disabled={loading}
          onClick={loadVoiceDrops}
        >
          {loading ? 'Loading...' : 'Refresh drops'}
        </button>
      </div>

      {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
      {message ? <div className="hitlab-alert">{message}</div> : null}

      <div className="hitlab-radio-feedback-rewards-summary">
        <article>
          <span>Pending review</span>
          <strong>{formatCount(summary.pendingReview)}</strong>
        </article>
        <article>
          <span>Approved</span>
          <strong>{formatCount(summary.approved)}</strong>
        </article>
        <article>
          <span>Rejected</span>
          <strong>{formatCount(summary.rejected)}</strong>
        </article>
      </div>

      {reviewRows.length ? (
        <div className="hitlab-radio-voice-drops-list">
          {reviewRows.map((row) => {
            const isSubmitted = row.status === 'submitted';
            const suggestedTitle = getSuggestedTitle(row);
            return (
              <article key={row.id} className="hitlab-radio-voice-drop-row">
                <div className="hitlab-radio-feedback-reward-row-main">
                  <div>
                    <strong>
                      {row.firstName || 'JG Player'} {row.cityRegion ? `from ${row.cityRegion}` : ''}
                    </strong>
                    <span>
                      {getDropTypeLabel(row.dropType)} - {getStatusLabel(row.status)}
                    </span>
                    <small>
                      Submitted {formatDate(row.submittedAt)}
                      {row.catalogItemId ? ` - Catalog ${row.catalogItemId}` : ''}
                    </small>
                  </div>
                  <div>
                    <span>Duration</span>
                    <strong>{formatCount(row.durationSeconds)}s</strong>
                    <small>{row.feedbackRewardPass || 'No pass reference'}</small>
                  </div>
                </div>

                {row.audioUrl ? (
                  <audio controls preload="metadata" src={row.audioUrl} />
                ) : null}

                {row.listenerScript ? (
                  <p className="hitlab-radio-voice-drop-script">
                    {row.listenerScript}
                  </p>
                ) : null}

                {isSubmitted ? (
                  <div className="hitlab-radio-feedback-reward-actions">
                    <label>
                      Approved title
                      <input
                        value={approvedTitles[row.id] || ''}
                        maxLength="160"
                        onChange={(event) => {
                          const { value } = event.target;
                          setApprovedTitles((current) => ({
                            ...current,
                            [row.id]: value,
                          }));
                        }}
                        placeholder={suggestedTitle}
                      />
                      <small>Suggested: {suggestedTitle}</small>
                    </label>
                    <label>
                      Reject reason
                      <input
                        value={rejectReasons[row.id] || ''}
                        onChange={(event) => {
                          const { value } = event.target;
                          setRejectReasons((current) => ({
                            ...current,
                            [row.id]: value,
                          }));
                        }}
                        placeholder="Required before rejecting"
                      />
                    </label>
                    <button
                      type="button"
                      className="hitlab-primary-btn"
                      disabled={busyId === row.id}
                      onClick={() => approveVoiceDrop(row)}
                    >
                      Approve to Radio
                    </button>
                    <button
                      type="button"
                      className="hitlab-secondary-btn"
                      disabled={busyId === row.id}
                      onClick={() => rejectVoiceDrop(row)}
                    >
                      Reject drop
                    </button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="hitlab-alert">No listener voice drops in review.</div>
      )}
    </section>
  );
}
